import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  styled,
  Typography,
  Menu,
  MenuItem,
} from '@mui/material';
import ApplicationBox from '../../components/Dashboard/CustomBox/ApplicationBox';
import {
  ButtonAppActive,
  ButtonAppPending,
  ButtonAppReject,
  ButtonAppDeactive,
  ButtonAppEnabled,
  ButtonAppInter,
  ButtonAppPublished,
  ButtonAppUnpublished,
} from '../../components/Dashboard/DashboardButton/ButtonApplications';
import useApplications from '../../hooks/useApplications';
import useIdentities from '../../hooks/useIdentities';
import { CardListSkeleton } from '../../components/Feedback/Loaders';
import { BRAND_BLUE, outlinedButtonSx, SPACING } from '../../theme/theme';
import Breadcrumb from '../../components/Navigation/Breadcrumb';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import {
  resetForm,
  setCurrentStep,
  setNextStep,
  updateApplicationField,
} from '../../Slice/unifiedFormSlice';
import PlusIcon from '/src/assets/images/button/plus-dashboard.svg?url';
import ArrowDownIcon from '/src/assets/images/stopper/identify.png?url';
import IdentifyIcon from '/src/assets/images/button/arrow-bar-to-down.svg?url';

const PageContainer = styled(Box)({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  marginTop: '0px',
  padding: '16px',
  backgroundColor: 'inherit',
});

const ContentWrapper = styled(Paper)({
  width: '950px',
  minHeight: '400px',
  padding: '16px',
  backgroundColor: 'inherit',
  boxShadow: 'none',
  direction: 'rtl',
});

const NavbarContainer = styled(Box)(() => ({
  width: '100%',
  maxWidth: '950px',
  display: 'flex',
  flexDirection: 'row-reverse',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0 20px',
  borderRadius: '8px',
  marginBottom: '20px',
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '8px',
  },
}));

const StyledButton = styled(Box)(() => ({
  fontFamily: 'IRANYekanXFaNum',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '16px',
  textTransform: 'none',
  width: '130px',
  height: '40px',
  borderRadius: '8px',
  padding: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '2px',
  ...outlinedButtonSx,
  '@media (max-width: 768px)': {
    width: '100%',
    fontSize: '0.9rem',
    marginTop: '3px',
  },
}));

const IdentitySelector = styled(Box)(() => ({
  fontFamily: 'IRANYekanXFaNum',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '16px',
  width: '250px',
  height: '40px',
  borderRadius: '8px',
  padding: '8px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  ...outlinedButtonSx,
  '@media (max-width: 768px)': {
    width: '100%',
    fontSize: '0.9rem',
  },
}));

const Applications: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const searchParams = new URLSearchParams(location.search);
  const initialPublisherId = searchParams.get('publisherId') || '';
  const [publisherId, setPublisherId] = useState(initialPublisherId);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const {
    data: applications,
    isLoading,
    isError,
    error,
  } = useApplications(publisherId);
  const { data: identities, isLoading: isIdentitiesLoading } = useIdentities();

  const handleInterButtonClick = (appId: string, publisherId: string) => {
    navigate(`/dashboard/application/${appId}`, {
      state: { appId, publisherId },
    });
  };


  const handleNewAppClick = () => {
    if (!publisherId) {
      navigate('/dashboard', {
        state: { error: 'لطفاً ابتدا یک هویت انتخاب کنید' },
      });
      return;
    }

    // Reset the whole form rather than clearing fields one by one — the
    // hand-written list missed application_file_url, so a previously uploaded
    // IPA stayed attached to the next application.
    dispatch(resetForm());
    dispatch(
      updateApplicationField({ field: 'publisher_id', value: publisherId }),
    );

    // تنظیم مرحله 2 برای اپلیکیشن جدید
    dispatch(setCurrentStep(2));
    dispatch(setNextStep(null));

    // پیدا کردن اطلاعات هویت انتخاب‌شده
    const selectedIdentity = identities?.find(
      (id) => id.id.toString() === publisherId,
    );

    navigate('/wizard', {
      state: {
        mode: 'new',
        id: publisherId,
        display_name: selectedIdentity?.display_name || '',
        identity_type: selectedIdentity?.name || 'PERSONAL',
        nextStep: 2,
      },
    });
  };

  const handleIdentitySelectClick = (
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleIdentitySelectClose = () => {
    setAnchorEl(null);
  };

  const handleIdentitySelect = (identityId: number) => {
    setPublisherId(identityId.toString());
    setAnchorEl(null);
    navigate(`${location.pathname}?publisherId=${identityId}`, {
      replace: true,
    });
  };

  if (isLoading || isIdentitiesLoading) {
    return (
      <PageContainer>
        <ContentWrapper>
          <CardListSkeleton count={3} height={140} />
        </ContentWrapper>
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer>
        <ContentWrapper>
          <Typography variant="body1">
            خطا:{' '}
            {error instanceof Error ? error.message : 'مشکلی پیش آمده است.'}
          </Typography>
        </ContentWrapper>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentWrapper>
        <Breadcrumb
          items={[
            { label: 'داشبورد', to: '/dashboard' },
            { label: 'اپلیکیشن‌ها' },
          ]}
        />
        <NavbarContainer style={{marginTop: '30px',
            padding:'10px',
            backgroundColor: 'white',
            borderRadius: '12px',
            border: 'none',
            boxShadow: '0 2px 10px rgba(16, 24, 40, 0.06)'}}>
          <IdentitySelector onClick={handleIdentitySelectClick}>
            <img
              src={ArrowDownIcon}
              alt="arrow down"
              style={{ width: '20px', height: '20px' }}
            />
            <Typography
              style={{
                fontFamily: 'IRANYekanXFaNum',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '16px',
              }}
            >
              {identities?.find((id) => id.id.toString() === publisherId)
                ?.display_name || 'انتخاب هویت'}
            </Typography>
            <img
              src={IdentifyIcon}
              alt="arrow down"
              style={{ width: '20px', height: '20px' }}
            />
          </IdentitySelector>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <StyledButton onClick={handleNewAppClick}>
              {/* No inline colour or filter: an inline `style` outranks the
                  button's CSS, so the icon and label kept the old blue and
                  stayed blue on hover instead of turning white. */}
              <img
                src={PlusIcon}
                alt="plus icon"
                style={{ width: '20px', height: '20px', padding: '0px' }}
              />
              <Typography
                sx={{
                  fontFamily: 'IRANYekanXFaNum',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '16px',
                  color: 'inherit',
                }}
              >
                اپلیکیشن جدید
              </Typography>
            </StyledButton>
          </Box>
        </NavbarContainer>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleIdentitySelectClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            style: {
              maxHeight: '300px',
              width: '250px',
              direction: 'rtl',
            },
          }}
        >
          {identities && identities.length > 0 ? (
            identities.map((identity) => {
              const isSelected = identity.id.toString() === publisherId;

              return (
                <MenuItem
                  key={identity.id}
                  selected={isSelected}
                  onClick={() => handleIdentitySelect(identity.id)}
                  sx={{
                    direction: 'rtl',
                    // Mui-focusVisible is also applied (the menu focuses the
                    // selected row on open) and its two-class selector outranks
                    // a plain &.Mui-selected, so it is matched explicitly here.
                    '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible':
                      {
                        backgroundColor: 'rgba(73, 148, 236, 0.12)',
                      },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'IRANYekanXFaNum',
                      fontWeight: isSelected ? 700 : 500,
                      fontSize: '14px',
                      lineHeight: '16px',
                      color: isSelected ? BRAND_BLUE : 'inherit',
                      textAlign: 'right',
                      width: '100%',
                    }}
                  >
                    {identity.display_name}
                  </Typography>
                </MenuItem>
              );
            })
          ) : (
            <MenuItem disabled style={{ direction: 'rtl' }}>
              <Typography
                style={{
                  fontFamily: 'IRANYekanXFaNum',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '16px',

                  textAlign: 'right',
                  width: '100%',
                }}
              >
                هویتی یافت نشد
              </Typography>
            </MenuItem>
          )}
        </Menu>

        {applications && applications.length > 0 ? (
          // SPACING.md is a pixel value; the theme sets spacing to 1. This was
          // a 1px gutter, hence the cards sitting tighter than other grids.
          <Grid container spacing={SPACING.md} mt={1}>
            {applications.map((app) => {
              // Two independent axes, each with its own badge so neither is
              // read as contradicting the other:
              //   review  — what sibche decided (در حال بررسی / تایید / رد)
              //   switch  — what the publisher chose (فعال / غیرفعال)
              // A published listing also gets a "منتشر شده" badge.
              //
              // Matching PENDING / APPROVED / REJECTED against `status` never
              // hit, because that field only holds PUBLISHED or UNPUBLISHED —
              // which is why every card previously read "غیرفعال".
              let ReviewButton;
              switch (app.statuses?.content) {
                case 'PENDING':
                  ReviewButton = <ButtonAppPending />;
                  break;
                case 'APPROVED':
                  ReviewButton = <ButtonAppActive />;
                  break;
                case 'REJECTED':
                  ReviewButton = <ButtonAppReject />;
                  break;
                default:
                  ReviewButton = null;
              }

              const AppButton = (
                <Box
                  sx={{
                    display: 'flex',
                    gap: '4px',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  {/* Always one of the two: `status` is the store's own
                      verdict (the `published` accessor), which "فعال" is
                      not — that is only the publisher's switch. Showing the
                      negative too is what stops "فعال" beside "درحال بررسی"
                      reading as a contradiction. */}
                  {app.status === 'PUBLISHED' ? (
                    <ButtonAppPublished />
                  ) : (
                    <ButtonAppUnpublished />
                  )}
                  {ReviewButton}
                  {Number(app.active) === 1 ? (
                    <ButtonAppEnabled />
                  ) : (
                    <ButtonAppDeactive />
                  )}
                </Box>
              );

              const InterButton = (
                <div
                  onClick={() =>
                    handleInterButtonClick(app.id.toString(), publisherId)
                  }
                  style={{ cursor: 'pointer' }}
                >
                  <ButtonAppInter />
                </div>
              );

              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={6}
                  key={app.id}
                  style={{ padding: '8px' }}
                >
                  <ApplicationBox
                    nameFa={app.name_fa}
                    nameEn={app.name_en}
                    version={`${app.version?.major ?? 0}.${app.version?.minor ?? 0}.${app.version?.patch ?? 0}.${app.version?.build ?? 0}`}
                    downloadCountText={app.download_count_text}
                    totalPrice={app.totalPrice}
                    logo={app.logo}
                    CustomButton={AppButton}
                    InterButton={InterButton}
                  />
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Typography variant="body1">
            اپلیکیشنی در این هویت یافت نشد.
          </Typography>
        )}
      </ContentWrapper>
    </PageContainer>
  );
};

export default Applications;
